import { useCallback, useRef, useState, type ReactNode } from 'react'
import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { Grid01, InfoCircle, UploadCloud01 } from '@untitled-ui/icons-react'
import { Group, callSearchClientOrganizations } from '@/api'
import DialogCloseButton from '@/components/DialogCloseButton'
import LoadingButton from '@/components/LoadingButton'
import useTranslate from '@/hooks/use-translate'
import { createParseWorker } from '@/sections/managementConsole/EstablishmentDetail/TableAutomationsSection/TableAutomationDetailFileView/TableAutomationDetailUpload/TableAutomationDetailUploadDropView/utils/createParseWorker'
import useFetchConfigurations from '@/hooks/use-fetch-configurations'
import { badgeTokens } from '@/theme/badge-tokens'
import { createToast } from '@/utils'
import { useQuery } from '@tanstack/react-query'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import type { CreateReferentialSource } from '../../types'
import { useCreateReferential } from '../../hooks/useCreateReferential'
import { sanitizeImportedGridData } from '../../utils/referentialGridPersistence'
import {
  referentialFormLabelSx,
  referentialSelectFormControlSx,
  referentialSelectSx,
} from '../../referentials-form-styles'

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024

type FieldLabelProps = {
  label: string
  showInfo?: boolean
  infoTitle?: string
}

const FieldLabel = ({ label, showInfo, infoTitle }: FieldLabelProps) => {
  const theme = useTheme()

  return (
    <Typography component="label" sx={referentialFormLabelSx}>
      {label}
      {showInfo && infoTitle ? (
        <Tooltip
          title={infoTitle}
          placement="top"
          arrow
          slotProps={{
            tooltip: {
              sx: { maxWidth: 320 },
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'help',
            }}
          >
            <SvgIcon
              sx={{
                fontSize: 14,
                color: theme.palette.primary.main,
              }}
            >
              <InfoCircle style={{ display: 'block' }} />
            </SvgIcon>
          </Box>
        </Tooltip>
      ) : null}
    </Typography>
  )
}

type FormFieldProps = {
  label: string
  showInfo?: boolean
  infoTitle?: string
  children: ReactNode
}

const FormField = ({ label, showInfo, infoTitle, children }: FormFieldProps) => (
  <Box>
    <FieldLabel label={label} showInfo={showInfo} infoTitle={infoTitle} />
    {children}
  </Box>
)

export type ReferentialCreateModalProps = {
  open: boolean
  onClose: () => void
  onCreated: () => void
  groups: Group[]
  userId?: string
}

export const ReferentialCreateModal = ({
  open,
  onClose,
  onCreated,
  groups,
  userId,
}: ReferentialCreateModalProps) => {
  const theme = useTheme()
  const { t } = useTranslate('referentials')
  const { t: tCommon } = useTranslate('common')
  const { createReferential, isCreating } = useCreateReferential()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [source, setSource] = useState<CreateReferentialSource>('import')
  const [gridData, setGridData] = useState<string[][]>([])
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>()
  const [loadingExcel, setLoadingExcel] = useState(false)
  const [importError, setImportError] = useState<string | undefined>()

  const showAgencyField = groups.length > 1

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('nameRequired')),
    groupId: Yup.string().required(t('agencyRequired')),
    configurationId: Yup.string().required(t('configurationRequired')),
    clientOrganizationId: Yup.string(),
    description: Yup.string(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      description: '',
      groupId: groups[0]?.gid ?? '',
      configurationId: '',
      clientOrganizationId: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!userId) {
        return
      }

      const effectiveSource: CreateReferentialSource =
        gridData.length > 0 ? 'import' : 'empty'

      try {
        await createReferential({
          name: values.name,
          description: values.description || undefined,
          groupId: values.groupId,
          userId,
          configurationId: values.configurationId,
          clientOrganizationId: values.clientOrganizationId || undefined,
          active: true,
          source: effectiveSource,
          gridData,
        })
        createToast('success', t('referentialCreated'))
        handleClose()
        onCreated()
      } catch {
        createToast('error', t('errorCreatingReferential'))
      }
    },
  })

  const { data: configurations = [] } = useFetchConfigurations({
    groupId: formik.values.groupId,
    enabled: !!formik.values.groupId,
    select: ({ data }) =>
      (data ?? []).filter(
        (configuration) => configuration.groupId === formik.values.groupId
      ),
  })

  const { data: clientOrganizations = [] } = useQuery({
    queryKey: ['referential-create-client-organizations', formik.values.groupId],
    queryFn: () => callSearchClientOrganizations({ gid: formik.values.groupId }),
    enabled: !!formik.values.groupId,
    select: ({ data }) => data ?? [],
  })

  const resetState = useCallback(() => {
    formik.resetForm()
    setSource('import')
    setGridData([])
    setUploadedFileName(undefined)
    setImportError(undefined)
  }, [formik])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [onClose, resetState])

  const parseExcelFile = async (file: File) => {
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setImportError(t('invalidFileType'))
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImportError(t('fileTooLarge'))
      return
    }

    setLoadingExcel(true)
    setImportError(undefined)

    try {
      const worker = createParseWorker()
      const u8 = new Uint8Array(await file.arrayBuffer())

      const parsedGrid = await new Promise<string[][]>((resolve, reject) => {
        worker.onmessage = (event) => {
          resolve(event.data)
          worker.terminate()
        }
        worker.onerror = () => {
          reject(new Error('PARSE_FAILED'))
          worker.terminate()
        }
        worker.postMessage(u8)
      })

      setGridData(sanitizeImportedGridData(parsedGrid))
      setUploadedFileName(file.name)
      setSource('import')
    } catch {
      createToast('error', t('errorCreatingReferential'))
    } finally {
      setLoadingExcel(false)
    }
  }

  const selectEmptyBase = () => {
    setSource('empty')
    setGridData([])
    setUploadedFileName(undefined)
    setImportError(undefined)
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const isEmptyBase = source === 'empty' && gridData.length === 0
  const hasUploadedFile = gridData.length > 0
  const isDataFileSelected = hasUploadedFile || isEmptyBase
  const referentialTitleLabel = formik.values.name.trim() || uploadedFileName

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogCloseButton onClose={handleClose} />

      <Box sx={{ px: 3, pt: 3, pb: 0 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: badgeTokens.green.bg,
              color: badgeTokens.green.fg,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <SvgIcon sx={{ fontSize: 20, color: 'inherit' }}>
              <Grid01 style={{ display: 'block' }} />
            </SvgIcon>
          </Box>
          <Box sx={{ pr: 4 }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                color: theme.palette.text.primary,
                lineHeight: 1.3,
              }}
            >
              {t('createModalTitle')}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 13,
                color: theme.palette.text.secondary,
                lineHeight: 1.5,
              }}
            >
              {t('createModalDescription')}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mt: 2.5 }} />
      </Box>

      <DialogContent
        sx={{
          px: 3,
          pt: '16px !important',
          pb: 1.5,
        }}
      >
        <Stack spacing={2}>
          <FormField label={t('fieldNameLabel')}>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              size="small"
              name="name"
              placeholder={t('fieldNamePlaceholder')}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name ? formik.errors.name : undefined}
            />
          </FormField>

          <FormField label={t('columnDescription')}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              name="description"
              placeholder={t('fieldDescriptionPlaceholder')}
              value={formik.values.description}
              onChange={formik.handleChange}
              multiline
              rows={2}
            />
          </FormField>

          {showAgencyField && (
            <FormField
              label={t('fieldAgencyLabel')}
              showInfo
              infoTitle={t('fieldAgencyTooltip')}
            >
              <FormControl
                fullWidth
                size="small"
                error={Boolean(formik.errors.groupId)}
                sx={referentialSelectFormControlSx}
              >
                <Select
                  name="groupId"
                  value={formik.values.groupId}
                  onChange={(event) => {
                    void formik.setFieldValue('groupId', event.target.value)
                    void formik.setFieldValue('configurationId', '')
                    void formik.setFieldValue('clientOrganizationId', '')
                  }}
                  displayEmpty
                  sx={referentialSelectSx}
                >
                  {groups.map((group) => (
                    <MenuItem key={group.gid} value={group.gid}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormField>
          )}

          <FormField
            label={t('fieldConfigurationLabel')}
            showInfo
            infoTitle={t('fieldConfigurationTooltip')}
          >
            <FormControl
              fullWidth
              size="small"
              error={
                formik.touched.configurationId &&
                Boolean(formik.errors.configurationId)
              }
              sx={referentialSelectFormControlSx}
            >
              <Select
                value={formik.values.configurationId}
                onChange={(event) => {
                  void formik.setFieldValue('configurationId', event.target.value)
                }}
                displayEmpty
                sx={referentialSelectSx}
              >
                <MenuItem value="" disabled>
                  {t('configurationRequired')}
                </MenuItem>
                {configurations.map((configuration) => (
                  <MenuItem key={configuration.cid} value={configuration.cid}>
                    {configuration.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.configurationId && formik.errors.configurationId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {formik.errors.configurationId}
                </Typography>
              )}
            </FormControl>
          </FormField>

          <FormField
            label={t('fieldDataFileLabel')}
            showInfo
            infoTitle={t('fieldDataFileTooltip')}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openFilePicker()
                }
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={async (event) => {
                event.preventDefault()
                const file = event.dataTransfer.files[0]
                if (file) {
                  await parseExcelFile(file)
                }
              }}
              sx={{
                border: '2px dashed',
                borderColor: isDataFileSelected
                  ? theme.palette.primary.main
                  : theme.palette.divider,
                borderRadius: '12px',
                bgcolor: isDataFileSelected
                  ? badgeTokens.green.bg
                  : theme.palette.background.paper,
                px: 2,
                py: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'border-color .15s ease, background-color .15s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: badgeTokens.green.bg,
                },
              }}
            >
              {loadingExcel ? (
                <CircularProgress size={28} sx={{ color: badgeTokens.green.fg }} />
              ) : (
                <Stack spacing={1} alignItems="center">
                  <SvgIcon sx={{ fontSize: 28, color: badgeTokens.green.fg }}>
                    <UploadCloud01 style={{ display: 'block' }} />
                  </SvgIcon>
                  {hasUploadedFile ? (
                    <>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {referentialTitleLabel}
                      </Typography>
                      {formik.values.name.trim() && uploadedFileName ? (
                        <Typography variant="caption" color="text.secondary">
                          {uploadedFileName}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        {t('rowsLoaded', { count: gridData.length })}
                      </Typography>
                    </>
                  ) : isEmptyBase ? (
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {t('emptyBaseSelected')}
                    </Typography>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {t('uploadZoneTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('uploadZoneFormats')}
                      </Typography>
                    </>
                  )}
                </Stack>
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={async (event) => {
                  const file = event.target.files?.[0]
                  if (file) {
                    await parseExcelFile(file)
                  }
                  event.target.value = ''
                }}
              />
            </Box>
            {importError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                {importError}
              </Typography>
            )}
            {!hasUploadedFile && (
              <Button
                variant="text"
                size="small"
                onClick={selectEmptyBase}
                sx={{
                  mt: 1,
                  px: 0,
                  fontSize: 13,
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {t('createEmptyBaseLink')}
              </Button>
            )}
          </FormField>

          <FormField
            label={t('fieldClientLabel')}
            showInfo
            infoTitle={t('fieldClientTooltip')}
          >
            <FormControl fullWidth size="small" sx={referentialSelectFormControlSx}>
              <Select
                value={formik.values.clientOrganizationId}
                onChange={(event) => {
                  void formik.setFieldValue(
                    'clientOrganizationId',
                    event.target.value
                  )
                }}
                displayEmpty
                sx={referentialSelectSx}
              >
                <MenuItem value="">{t('clientIndependentOption')}</MenuItem>
                {clientOrganizations.map((clientOrganization) => (
                  <MenuItem
                    key={clientOrganization.coid}
                    value={clientOrganization.coid}
                  >
                    {clientOrganization.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.secondary,
          }}
        >
          {tCommon('cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          isLoading={isCreating}
          startIcon={<AddIcon />}
          onClick={() => formik.handleSubmit()}
          sx={{
            borderRadius: '8px',
            px: 2,
            fontWeight: 600,
            boxShadow: 'none',
          }}
        >
          {t('createReferentialButton')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
